import { forwardRef, memo, useEffect, useMemo, useRef, useState } from 'react';
import '../../../shared/styles/Orchestrator.css';
import { getImage } from '../utils/storage';
import EffectRenderer from './EffectRenderer';
import Loading from '../../../shared/components/Loading';
import { findCondition } from '../data/conditionDefinitions';
import {
  getGridTooltipPosition,
  getTokenInitialFontSize,
  hexCellToPercent,
  nearestHexCell,
} from '../utils/gridConfig';
import {
  DEFAULT_GRID_VIEW_STATE,
  fitGridContentToViewport,
  MAX_GRID_ZOOM,
  normalizeGridViewState,
} from '../utils/gridViewState';
import { applyBattleEffectCellChanges } from '../utils/battleEffectCells';
import DaggerheartHealthTooltip from './DaggerheartHealthTooltip';
import GridBattleEffectsCanvas from './GridBattleEffectsCanvas';
import GridTokenImageCanvas from './GridTokenImageCanvas';
import GridViewportPreview from './GridViewportPreview';
import GridWorldCanvas from './GridWorldCanvas';

const RANGE_STYLES = [
  { label: 'Melee', max: 4.9, color: '#ff2d55' },
  { label: 'Very Close', max: 10, color: '#ff8a00' },
  { label: 'Close', max: 30, color: '#ffd400' },
  { label: 'Far', max: 100, color: '#1dd36f' },
  { label: 'Very Far', max: 300, color: '#2f6bff' },
];

const categorizeRange = (feet) => {
  for (const entry of RANGE_STYLES) {
    if (feet <= entry.max) {
      return entry;
    }
  }
  return RANGE_STYLES[RANGE_STYLES.length - 1];
};

const PRESENTING_KEY = 'presenting';
const PRESENTING_SIZE = 10;

const isPresentingSizeValue = (value) => {
  if (typeof value === 'string') {
    return value.toLowerCase() === PRESENTING_KEY;
  }
  return Number(value) === PRESENTING_SIZE;
};

const clampStandardSizeValue = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 1;
  return Math.min(4, Math.max(1, num));
};

// Hex token size values map to approximate grid units
const HEX_SIZE_GRID_UNITS = { hex1: 1, hex3: 2, hex7: 3, hex12: 4 };

const getHexTokenGridUnits = (value) => {
  if (typeof value === 'string') {
    const lower = value.toLowerCase();
    if (lower in HEX_SIZE_GRID_UNITS) return HEX_SIZE_GRID_UNITS[lower];
  }
  return null;
};

const getTokenGridUnits = (value) => {
  const hexUnits = getHexTokenGridUnits(value);
  if (hexUnits !== null) return hexUnits;
  return isPresentingSizeValue(value) ? PRESENTING_SIZE : clampStandardSizeValue(value);
};

// Hex distance helpers using even-q offset coordinates (flat-top orientation)
const hexOffsetToCube = (q, r) => {
  const x = q;
  const z = r - (q - (q & 1)) / 2;
  const y = -x - z;
  return { x, y, z };
};

const hexCubeDistance = (a, b) =>
  (Math.abs(a.x - b.x) + Math.abs(a.y - b.y) + Math.abs(a.z - b.z)) / 2;

const hexGridDistance = (q1, r1, q2, r2) =>
  hexCubeDistance(hexOffsetToCube(q1, r1), hexOffsetToCube(q2, r2));

// Shared constant so sqrt(3) is evaluated once at module load.
const HEX_SQRT3 = Math.sqrt(3);

/**
 * Centre of hex cell (col, row) in flat-top even-q offset SVG coordinates
 * where the viewBox origin is (0, 0) and the unit size is 1 hex radius.
 */
const hexCenterForSvg = (col, row) => ({
  x: 1 + col * 1.5,
  y: HEX_SQRT3 / 2 + row * HEX_SQRT3 + (col % 2 === 1 ? HEX_SQRT3 / 2 : 0),
});

const CONDITION_RING_RADIUS = 50;
const CONDITION_RING_STROKE_WIDTH = 4;
const CONDITION_RING_ICON_RADIUS = 50;

const polarToCartesian = (radius, angleInDegrees) => {
  const angleInRadians = (angleInDegrees * Math.PI) / 180;
  return {
    x: 50 + Math.cos(angleInRadians) * radius,
    y: 50 + Math.sin(angleInRadians) * radius,
  };
};

const describeRingSegment = (radius, startAngle, endAngle) => {
  const start = polarToCartesian(radius, startAngle);
  const end = polarToCartesian(radius, endAngle);
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;
};

const getConditionIconPosition = (index, total) => {
  if (total === 1) {
    return { x: 50, y: 50 - CONDITION_RING_ICON_RADIUS };
  }
  const angle = ((-90 + (360 / total) * (index + 0.5)) * Math.PI) / 180;
  return {
    x: 50 + Math.cos(angle) * CONDITION_RING_ICON_RADIUS,
    y: 50 + Math.sin(angle) * CONDITION_RING_ICON_RADIUS,
  };
};

function TokenConditionRing({ conditions, system, tokenId }) {
  const condDefs = conditions
    .map((id) => findCondition(system, id))
    .filter(Boolean)
    .filter((condition, index, list) => list.findIndex((entry) => entry.id === condition.id) === index);

  if (condDefs.length === 0) return null;

  return (
    <div className='token_condition_ring' aria-hidden='true' data-token-id={tokenId}>
      <svg className='token_condition_ring_svg' viewBox='0 0 100 100'>
        <circle
          className='token_condition_ring_outline'
          cx='50'
          cy='50'
          r={CONDITION_RING_RADIUS}
          fill='none'
          stroke='rgba(0, 0, 0, 0.62)'
          strokeWidth={CONDITION_RING_STROKE_WIDTH + 2}
          vectorEffect='non-scaling-stroke'
        />
        {condDefs.length === 1 ? (
          <circle
            cx='50'
            cy='50'
            r={CONDITION_RING_RADIUS}
            fill='none'
            stroke={condDefs[0].color}
            strokeWidth={CONDITION_RING_STROKE_WIDTH}
            strokeLinecap='round'
            vectorEffect='non-scaling-stroke'
          />
        ) : (
          condDefs.map((condition, index) => {
            const segmentAngle = 360 / condDefs.length;
            const startAngle = -90 + segmentAngle * index;
            const endAngle = startAngle + segmentAngle;

            return (
            <path
              key={`${tokenId}-${condition.id}`}
              className='token_condition_ring_segment'
              d={describeRingSegment(CONDITION_RING_RADIUS, startAngle, endAngle)}
              fill='none'
              stroke={condition.color}
              strokeWidth={CONDITION_RING_STROKE_WIDTH}
              strokeLinecap='butt'
              vectorEffect='non-scaling-stroke'
            />
            );
          })
        )}
      </svg>
      {condDefs.map((condition, index) => {
        const { x, y } = getConditionIconPosition(index, condDefs.length);
        const Icon = condition.icon;

        return (
          <span
            key={`icon-${tokenId}-${condition.id}`}
            className='token_condition_icon'
            style={{
              left: `${x}%`,
              top: `${y}%`,
              color: condition.color,
              borderColor: condition.color,
            }}
            title={condition.label}
          >
            {Icon ? <Icon size={12} strokeWidth={2.25} /> : null}
          </span>
        );
      })}
    </div>
  );
}

const GridBoard = memo(forwardRef(function GridBoard(
  {
    scene,
    onDragToken,
    dragVisualScale = 1,
    onTokenDoubleClick,
    rangeSourceId,
    readOnly = false,
    selectedTokenId,
    selectedLayerId = null,
    onSelectToken,
    editingBattleEffectId,
    onPaintBattleEffectCells,
    pings = [],
    onPingGrid,
    onPingDoubleClick,
    showNames = false,
    gridCols = 24,
    gridRows = 30,
    gridType = 'square',
    dhTooltipTokenId = null,
    onDhTokenUpdate,
    onDhTooltipClose,
    boardOverlayScale = 1,
    pingZoomScale = 1,
    drawingLayerId = null,
    onDrawLayerStroke,
    onMoveTextLayer,
    viewportPreviewStore = null,
    onMoveDisplayViewport,
    boardTransform,
    boardDimensions,
    keepDisplayViewportPreview = false,
    displayViewportInteractive = false,
    viewState = DEFAULT_GRID_VIEW_STATE,
    fitMode = 'contain',
    onViewStateChange,
    cameraInteractive = false,
    cameraWheelZoom = false,
  },
  forwardedRef
) {
  const localRef = useRef(null);
  const worldOverlayRef = useRef(null);
  const viewportOverlayRef = useRef(null);
  const cameraDragRef = useRef(null);
  const cameraViewRef = useRef(normalizeGridViewState(viewState));
  const gridAspectRatio = gridType === 'hex'
    ? (1.5 * gridCols + 0.5) / (HEX_SQRT3 * (gridRows + 0.5))
    : gridCols / gridRows;
  const getCameraViewport = (rect = localRef.current?.getBoundingClientRect()) => (
    rect?.width && rect?.height
      ? { width: rect.width, height: rect.height, aspectRatio: gridAspectRatio, fitMode }
      : null
  );
  const getGridContent = (width, height) => (
    fitGridContentToViewport(width, height, gridAspectRatio, fitMode)
  );
  const fadeTimersRef = useRef({ enter: new Map(), exit: new Map() });
  const lastDisplayPosRef = useRef(new Map());
  const paintStateRef = useRef({ active: false, add: true });
  const paintChangesRef = useRef(new Map());
  const lastPaintedHexRef = useRef(null);
  const prevSceneIdRef = useRef(null);
  const imageCacheRef = useRef(new Map());
  // Draw layer state
  const drawStrokeRef = useRef(null); // { layerId, pointerId, points }
  const [liveStroke, setLiveStroke] = useState(null); // { layerId, points }
  // Text layer drag state
  const textDragRef = useRef(null); // { layerId, pointerId, startClientX, startClientY, startLayerX, startLayerY }
  const [textDragPos, setTextDragPos] = useState(null); // { layerId, x, y }
  const [livePaintChanges, setLivePaintChanges] = useState([]);
  // Suppress the next grid click after a text drag or draw stroke completes
  const suppressNextClickRef = useRef(false);
  const suppressNextDoubleClickRef = useRef(false);

  const attachRef = (node) => {
    localRef.current = node;
    if (!forwardedRef) return;
    if (typeof forwardedRef === 'function') {
      forwardedRef(node);
    } else {
      forwardedRef.current = node;
    }
  };

  const [layerUrlMap, setLayerUrlMap] = useState({}); // { [layerId]: url }
  const [tokenUrls, setTokenUrls] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [animateMoves, setAnimateMoves] = useState(false);
  const [tokenTransforms, setTokenTransforms] = useState({});
  const [dragVisual, setDragVisual] = useState(null); // { id, dx, dy }
  const dragStartRef = useRef(null); // { id, pointerId, startX, startY }
  const previousVisibleRef = useRef(new Set());
  const previousReadOnlyRef = useRef(readOnly);
  const initialReadOnlyRef = useRef(readOnly);
  const [enteringTokens, setEnteringTokens] = useState([]); // {id, addedAt}
  const [exitingTokens, setExitingTokens] = useState([]); // {id, token, lastDisplayPos, addedAt}
  const previousVisibleSceneIdRef = useRef(scene.id);
  const battleEffects = useMemo(() => {
    if (!scene || !Array.isArray(scene.battleEffects)) return [];
    return scene.battleEffects.filter(Boolean);
  }, [scene]);
  const editingEffect = useMemo(
    () => battleEffects.find((effect) => effect.id === editingBattleEffectId) || null,
    [battleEffects, editingBattleEffectId]
  );
  const editingCellSet = useMemo(() => {
    const set = new Set();
    if (!editingEffect || !Array.isArray(editingEffect.cells)) return set;
    editingEffect.cells.forEach((cell) => {
      if (cell && Number.isFinite(cell.x) && Number.isFinite(cell.y)) {
        set.add(`${cell.x}|${cell.y}`);
      }
    });
    return set;
  }, [editingEffect]);
  const visibleBattleEffects = useMemo(
    () => battleEffects.filter((effect) => effect && effect.visible !== false && Array.isArray(effect.cells)),
    [battleEffects]
  );
  const battleEffectsForCanvas = useMemo(() => {
    if (editingEffect && editingEffect.visible === false) {
      return [...visibleBattleEffects, { ...editingEffect, opacity: 0.18 }];
    }
    return visibleBattleEffects;
  }, [editingEffect, visibleBattleEffects]);
  const renderedBattleEffects = useMemo(
    () => battleEffectsForCanvas.map((effect) => (
      effect.id === editingEffect?.id && livePaintChanges.length
        ? {
            ...effect,
            cells: applyBattleEffectCellChanges(
              effect.cells,
              livePaintChanges,
              gridCols,
              gridRows
            ),
          }
        : effect
    )),
    [battleEffectsForCanvas, editingEffect?.id, gridCols, gridRows, livePaintChanges]
  );

  useEffect(() => {
    cameraViewRef.current = normalizeGridViewState(viewState, getCameraViewport());
  }, [viewState?.centerX, viewState?.centerY, viewState?.zoom, gridAspectRatio, fitMode]);

  useEffect(() => {
    paintChangesRef.current.clear();
    setLivePaintChanges([]);
    paintStateRef.current = { active: false, add: true };
  }, [editingEffect?.id]);

  // ─── Flat-top hex geometry constants (null in square mode) ────────────────
  const hexSvgW = gridType === 'hex' ? (1.5 * gridCols + 0.5) : null;
  const hexSvgH = gridType === 'hex' ? (HEX_SQRT3 * (gridRows + 0.5)) : null;
  // Width/height of one hex cell as a percentage of the board dimensions
  const hexCellWPct = gridType === 'hex' ? (200 / (1.5 * gridCols + 0.5)) : null;
  const hexCellHPct = gridType === 'hex' ? (100 / (gridRows + 0.5)) : null;
  const overlayCounterScale = 1 / Math.max(Number(boardOverlayScale) || 1, 0.01);

  const rangeMeasurements = useMemo(() => {
    if (!rangeSourceId) return [];
    const tokens = scene.tokens || [];
    const source = tokens.find((token) => token.id === rangeSourceId && token.visible !== false);
    if (!source) return [];

    // For hex mode, SVG coordinates use the hex viewBox space (svgW × svgH).
    // For square mode, SVG coordinates are in grid-cell units (gridCols × gridRows).
    const hexModeLocal = gridType === 'hex';
    const hexSvgWLocal = hexModeLocal ? (1.5 * gridCols + 0.5) : null;
    const hexSvgHLocal = hexModeLocal ? (HEX_SQRT3 * (gridRows + 0.5)) : null;

    const rectFromToken = (token) => {
      const size = getTokenGridUnits(token.size);
      const minX = token.x ?? 0;
      const minY = token.y ?? 0;
      let centerX, centerY;
      if (hexModeLocal) {
        const c = hexCenterForSvg(minX, minY);
        centerX = c.x;
        centerY = c.y;
      } else {
        centerX = minX + size / 2;
        centerY = minY + size / 2;
      }
      return { minX, minY, maxX: minX + size, maxY: minY + size, size, centerX, centerY };
    };

    const rectSource = rectFromToken(source);
    const centerSource = { x: rectSource.centerX, y: rectSource.centerY };

    const axisGap = (minA, maxA, minB, maxB) => {
      if (minB >= maxA) return minB - maxA;
      if (minA >= maxB) return minA - maxB;
      return 0;
    };

    return tokens
      .filter((token) => token.id !== source.id && token.visible !== false)
      .map((token) => {
        const rectTarget = rectFromToken(token);
        const centerTarget = { x: rectTarget.centerX, y: rectTarget.centerY };

        const lineStart = centerSource;
        const lineEnd = centerTarget;

        let feetFloat;
        if (hexModeLocal) {
          // Hex distance: cube-coordinate distance between closest hex cells of each token
          const sourceHexSize = rectSource.size;
          const targetHexSize = rectTarget.size;
          // Approximate "radius" in hex steps (half of occupied hexes span)
          const srcRadius = Math.max(0, Math.floor((sourceHexSize - 1) / 2));
          const tgtRadius = Math.max(0, Math.floor((targetHexSize - 1) / 2));
          const rawHexDist = hexGridDistance(
            Math.round(rectSource.minX + srcRadius),
            Math.round(rectSource.minY + srcRadius),
            Math.round(rectTarget.minX + tgtRadius),
            Math.round(rectTarget.minY + tgtRadius)
          );
          const gapDist = Math.max(0, rawHexDist - srcRadius - tgtRadius);
          feetFloat = gapDist * 5;
        } else {
          const dx = axisGap(rectSource.minX, rectSource.maxX, rectTarget.minX, rectTarget.maxX);
          const dy = axisGap(rectSource.minY, rectSource.maxY, rectTarget.minY, rectTarget.maxY);
          feetFloat = Math.hypot(dx, dy) * 5;
        }

        // Scale label offset proportionally to the viewBox size
        const viewW = hexModeLocal ? hexSvgWLocal : gridCols;
        const viewH = hexModeLocal ? hexSvgHLocal : gridRows;
        const feet = Math.max(0, Math.round(feetFloat));
        const { label, color } = categorizeRange(feetFloat);
        const angle = Math.atan2(lineEnd.y - lineStart.y, lineEnd.x - lineStart.x);
        const midX = (lineStart.x + lineEnd.x) / 2;
        const midY = (lineStart.y + lineEnd.y) / 2;
        const offset = Math.min(viewW, viewH) * 0.012 / Math.max(Number(boardOverlayScale) || 1, 0.01);
        const perpAngle = angle + Math.PI / 2;
        const labelX = midX + Math.cos(perpAngle) * offset;
        const labelY = midY + Math.sin(perpAngle) * offset;
        let rotation = (angle * 180) / Math.PI;
        if (rotation > 180) rotation -= 360;
        if (rotation <= -180) rotation += 360;

        return {
          targetId: token.id,
          lineStart,
          lineEnd,
          feet,
          label,
          color,
          labelX,
          labelY,
          rotation,
          viewW,
          viewH,
        };
      });
  }, [rangeSourceId, scene, gridType, gridCols, gridRows, boardOverlayScale]);

  const namePositions = useMemo(() => {
    if (!showNames) return [];
    const hexMode = gridType === 'hex';
    const viewW = hexMode ? (1.5 * gridCols + 0.5) : gridCols;
    const viewH = hexMode ? (HEX_SQRT3 * (gridRows + 0.5)) : gridRows;
    // Scale font metrics relative to a 24-column square grid (the default grid
    // width) so that labels remain legible at any col count and in hex mode.
    const unitScale = viewW / 24;
    const FONT_SIZE = 0.45 * unitScale;
    const CHAR_WIDTH = FONT_SIZE * 0.62 * overlayCounterScale;
    const LH = FONT_SIZE * 1.4 * overlayCounterScale;
    const GAP = 0.08 * unitScale * overlayCounterScale;

    const visibleTokens = scene.tokens.filter((t) => t.visible !== false);
    const placed = [];

    return visibleTokens.map((token) => {
      const size = getTokenGridUnits(token.size);
      let cx, cy;
      if (hexMode) {
        const col = token.x ?? 0;
        const row = token.y ?? 0;
        cx = 1 + col * 1.5;
        cy = HEX_SQRT3 / 2 + row * HEX_SQRT3 + (col % 2 === 1 ? HEX_SQRT3 / 2 : 0);
      } else {
        cx = (token.x ?? 0) + size / 2;
        cy = (token.y ?? 0) + size / 2;
      }
      const name = (token.name || '').trim() || '?';
      const lw = Math.max(
        0.6 * unitScale * overlayCounterScale,
        name.length * CHAR_WIDTH + 0.22 * unitScale * overlayCounterScale
      );

      // Build candidate positions spiralling outward from the token in all directions.
      // Each candidate is the center point (lx, ly) of the label bounding box.
      const candidates = [];
      const DIRECTIONS = [
        [0, -1],   // N
        [0, 1],    // S
        [1, 0],    // E
        [-1, 0],   // W
        [-0.7, -0.7], // NW
        [0.7, -0.7],  // NE
        [-0.7, 0.7],  // SW
        [0.7, 0.7],   // SE
      ];
      const baseRadius = (hexMode ? unitScale : size / 2) + LH / 2 + GAP;
      for (let ring = 0; ring < 6; ring++) {
        const r = baseRadius + ring * (LH + GAP);
        for (const [nx, ny] of DIRECTIONS) {
          candidates.push({ lx: cx + nx * r, ly: cy + ny * r });
        }
      }

      let bestLx = cx;
      let bestLy = cy - baseRadius;
      let bestBox = { x: bestLx - lw / 2, y: bestLy - LH / 2, w: lw, h: LH };
      let found = false;

      for (const { lx, ly } of candidates) {
        const box = { x: lx - lw / 2, y: ly - LH / 2, w: lw, h: LH };
        const overlaps = placed.some(
          (p) =>
            p.x < box.x + box.w &&
            p.x + p.w > box.x &&
            p.y < box.y + box.h &&
            p.y + p.h > box.y
        );
        if (!overlaps) {
          bestLx = lx;
          bestLy = ly;
          bestBox = box;
          found = true;
          break;
        }
      }

      if (!found) {
        bestBox = { x: bestLx - lw / 2, y: bestLy - LH / 2, w: lw, h: LH };
      }
      placed.push(bestBox);
      return { id: token.id, labelX: bestLx, labelY: bestLy, name, viewW, viewH, fontSize: FONT_SIZE };
    });
  }, [showNames, scene.tokens, gridType, gridCols, gridRows, overlayCounterScale]);

  useEffect(() => {
    let cancelled = false;
    const sceneChanged = scene.id !== prevSceneIdRef.current;
    prevSceneIdRef.current = scene.id;
    if (sceneChanged) setIsLoading(true);

    const resolveImages = async () => {
      const imageLayers = scene.layers.filter(
        (l) => !l.type || l.type === 'image'
      );
      const urlPairs = await Promise.all(
        imageLayers.map(async (layer) => {
          if (layer.dataUrl) return [layer.id, layer.dataUrl];
          // Support remote imageUrl (from gameplay sync / player scene payload)
          if (layer.imageUrl) return [layer.id, layer.imageUrl];
          if (layer.imageId) {
            const cached = imageCacheRef.current.get(layer.imageId);
            if (cached) return [layer.id, cached];
            try {
              const url = await getImage(layer.imageId);
              if (url) imageCacheRef.current.set(layer.imageId, url);
              return [layer.id, url];
            } catch {
              return [layer.id, null];
            }
          }
          return [layer.id, null];
        })
      );

      if (!cancelled) {
        setLayerUrlMap(Object.fromEntries(urlPairs));
      }

      const tokenEntries = await Promise.all(
        scene.tokens.map(async (token) => {
          // Support remote image URLs (from gameplay sync / player scene payload)
          const goodUrl = token.goodImageUrl || null;
          const evilUrl = token.evilImageUrl || null;
          const neutralUrl = token.neutralImageUrl || null;
          let selectedRemoteUrl = goodUrl;
          if (token.disposition === 'evil') {
            selectedRemoteUrl = evilUrl || goodUrl;
          } else if (token.disposition === 'neutral') {
            selectedRemoteUrl = neutralUrl || null;
          }
          if (selectedRemoteUrl) return [token.id, selectedRemoteUrl];

          const goodId = token.goodImageId || token.imageId || null;
          const evilId = token.evilImageId || token.altImageId || null;
          const neutralId = token.neutralImageId || null;
          let selectedImageId = goodId;
          if (token.disposition === 'evil') {
            selectedImageId = evilId || goodId;
          } else if (token.disposition === 'neutral') {
            selectedImageId = neutralId || null; // neutral shows placeholder when missing
          }
          if (!selectedImageId) return [token.id, null];
          const cached = imageCacheRef.current.get(selectedImageId);
          if (cached) return [token.id, cached];
          try {
            const url = await getImage(selectedImageId);
            if (url) imageCacheRef.current.set(selectedImageId, url);
            return [token.id, url];
          } catch {
            return [token.id, null];
          }
        })
      );

      if (!cancelled) {
        setTokenUrls(Object.fromEntries(tokenEntries));
        setIsLoading(false);
      }
    };

    resolveImages();
    return () => {
      cancelled = true;
    };
  }, [scene]);

  // Enable animation after initial mount for display-only (readOnly) mode to avoid first-load slide-in
  useEffect(() => {
    if (!readOnly || !initialReadOnlyRef.current) return undefined;
    const id = requestAnimationFrame(() => setAnimateMoves(true));
    return () => cancelAnimationFrame(id);
  }, [readOnly]);

  // Compute transform positions for display-only mode to avoid layout thrash from left/top
  useEffect(() => {
    if (!readOnly) return;
    if (!localRef.current) return;
    const lastSizeRef = { w: null, h: null };
    let rafId = null;

    const computeTransforms = () => {
      const board = localRef.current;
      if (!board) return;
      const width = board.offsetWidth;
      const height = board.offsetHeight;
      if (!width || !height) return;
      const sizeChanged = lastSizeRef.w !== width || lastSizeRef.h !== height;
      const visibleTokens = (scene.tokens || []).filter((t) => t.visible !== false);
      const map = {};
      const content = getGridContent(width, height);
      const hexCellW = gridType === 'hex' ? (200 / (1.5 * gridCols + 0.5)) : null;
      const hexCellH = gridType === 'hex' ? (100 / (gridRows + 0.5)) : null;
      visibleTokens.forEach((t) => {
        let pos;
        if (gridType === 'hex') {
          const tokenSize = getTokenGridUnits(t.size);
          const { pctX, pctY } = hexCellToPercent(t.x, t.y, gridCols, gridRows);
          pos = {
            x: ((pctX - tokenSize * hexCellW / 2) / 100) * content.width,
            y: ((pctY - tokenSize * hexCellH / 2) / 100) * content.height,
          };
        } else {
          pos = {
            x: (t.x / gridCols) * content.width,
            y: (t.y / gridRows) * content.height,
          };
        }
        map[t.id] = pos;
        lastDisplayPosRef.current.set(t.id, pos);
      });

      setTokenTransforms((prev) => {
        const prevKeys = Object.keys(prev);
        const sameLength = prevKeys.length === visibleTokens.length;
        let identical = sameLength;
        if (identical) {
          for (const token of visibleTokens) {
            const prevEntry = prev[token.id];
            const currEntry = map[token.id];
            if (!prevEntry || !currEntry || prevEntry.x !== currEntry.x || prevEntry.y !== currEntry.y) {
              identical = false;
              break;
            }
          }
        }
        if (!sizeChanged && identical) return prev;
        return map;
      });

      lastSizeRef.w = width;
      lastSizeRef.h = height;
    };

    computeTransforms();
    const handleResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(computeTransforms);
    };
    window.addEventListener('resize', handleResize);
    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(localRef.current);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
      resizeObserver?.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [scene.tokens, readOnly, gridType, gridCols, gridRows, gridAspectRatio]);

  // Cleanup any pending fade timers on unmount
  useEffect(() => {
    return () => {
      const { enter, exit } = fadeTimersRef.current;
      for (const timeoutId of enter.values()) clearTimeout(timeoutId);
      for (const timeoutId of exit.values()) clearTimeout(timeoutId);
      enter.clear();
      exit.clear();
    };
  }, []);

  // Track visibility changes for fade-in/out (opacity only)
  useEffect(() => {
    const currentVisible = new Set(
      scene.tokens.filter((t) => t.visible !== false).map((t) => t.id)
    );
    const sceneChanged = previousVisibleSceneIdRef.current !== scene.id;
    previousVisibleSceneIdRef.current = scene.id;
    const controlsJustDisabled = readOnly && !previousReadOnlyRef.current;
    previousReadOnlyRef.current = readOnly;
    if (sceneChanged) {
      const { enter, exit } = fadeTimersRef.current;
      for (const timeoutId of enter.values()) clearTimeout(timeoutId);
      for (const timeoutId of exit.values()) clearTimeout(timeoutId);
      enter.clear();
      exit.clear();
      setEnteringTokens([]);
      setExitingTokens([]);
      previousVisibleRef.current = new Set();
    }
    if (controlsJustDisabled) {
      previousVisibleRef.current = currentVisible;
      setEnteringTokens([]);
      return;
    }
    const prevVisible = previousVisibleRef.current;
    const now = Date.now();
    const fadeDuration = 400; // ms

    const clearEnterTimer = (id) => {
      const timeoutId = fadeTimersRef.current.enter.get(id);
      if (timeoutId) clearTimeout(timeoutId);
      fadeTimersRef.current.enter.delete(id);
    };

    const clearExitTimer = (id) => {
      const timeoutId = fadeTimersRef.current.exit.get(id);
      if (timeoutId) clearTimeout(timeoutId);
      fadeTimersRef.current.exit.delete(id);
    };

    // Entering tokens
    const newlyEntering = Array.from(currentVisible).filter(
      (id) => !prevVisible.has(id)
    );
    if (newlyEntering.length) {
      // If a token is coming back while an exit clone is fading out, remove the exit clone immediately.
      setExitingTokens((old) => old.filter((entry) => !newlyEntering.includes(entry.id)));

      setEnteringTokens((old) => {
        const existingIds = new Set(old.map((e) => e.id));
        const toAdd = newlyEntering
          .filter((id) => !existingIds.has(id))
          .map((id) => ({ id, addedAt: now }));
        return [...old, ...toAdd];
      });

      newlyEntering.forEach((id) => {
        // Cancel any pending exit/enter cleanup for this token.
        clearExitTimer(id);
        clearEnterTimer(id);
        const timeoutId = setTimeout(() => {
          setEnteringTokens((old) => old.filter((e) => e.id !== id));
          fadeTimersRef.current.enter.delete(id);
        }, fadeDuration);
        fadeTimersRef.current.enter.set(id, timeoutId);
      });
    }
    // Exiting tokens
    const newlyExiting = sceneChanged
      ? []
      : Array.from(prevVisible).filter((id) => !currentVisible.has(id));
    if (newlyExiting.length) {
      // If a token is exiting, cancel any in-progress enter animation.
      setEnteringTokens((old) => old.filter((entry) => !newlyExiting.includes(entry.id)));

      setExitingTokens((old) => {
        const existingIds = new Set(old.map((e) => e.id));
        const toAdd = newlyExiting
          .filter((id) => !existingIds.has(id))
          .map((id) => {
            const token = scene.tokens.find((t) => t.id === id);
            const lastDisplayPos = lastDisplayPosRef.current.get(id) || null;
            return {
              id,
              token: token
                ? { ...token }
                : { id, x: 0, y: 0, grayscale: false, showInitial: false },
              lastDisplayPos,
              addedAt: now,
            };
          });
        return [...old, ...toAdd];
      });

      newlyExiting.forEach((id) => {
        clearEnterTimer(id);
        clearExitTimer(id);
        const timeoutId = setTimeout(() => {
          setExitingTokens((old) => old.filter((e) => e.id !== id));
          fadeTimersRef.current.exit.delete(id);
          lastDisplayPosRef.current.delete(id);
        }, fadeDuration);
        fadeTimersRef.current.exit.set(id, timeoutId);
      });
    }
    previousVisibleRef.current = currentVisible;
  }, [readOnly, scene.tokens]);

  const handleTokenPointerDown = (event, tokenId) => {
    if (readOnly || event.pointerType === 'touch') return;
    event.stopPropagation();
    event.preventDefault();

    dragStartRef.current = {
      id: tokenId,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
    };
    setDragVisual({ id: tokenId, dx: 0, dy: 0 });
    document.body.classList.add('token-dragging');

    const el = event.currentTarget;
    if (el?.setPointerCapture && event.pointerId != null) {
      try {
        el.setPointerCapture(event.pointerId);
      } catch {
        // ignore
      }
    }
  };

  const handleTokenPointerMove = (event, tokenId) => {
    if (readOnly) return;
    const drag = dragStartRef.current;
    if (!drag || drag.id !== tokenId) return;
    if (drag.pointerId != null && event.pointerId != null && drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    const scaleValue = Number(dragVisualScale);
    const effectiveScale = Number.isFinite(scaleValue) && scaleValue > 0 ? scaleValue : 1;
    const dx = (event.clientX - drag.startX) / effectiveScale;
    const dy = (event.clientY - drag.startY) / effectiveScale;
    setDragVisual({ id: tokenId, dx, dy });
  };

  const finishTokenDrag = (event, tokenId) => {
    if (readOnly) return;
    const drag = dragStartRef.current;
    if (!drag || drag.id !== tokenId) return;

    event.preventDefault();
    document.body.classList.remove('token-dragging');

    if (onDragToken) onDragToken(tokenId, getWorldPoint(event.clientX, event.clientY));

    dragStartRef.current = null;
    setDragVisual(null);
  };

  const getWorldPoint = (clientX, clientY) => {
    const board = localRef.current;
    if (!board) return null;
    const rect = board.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const content = getGridContent(rect.width, rect.height);
    const camera = cameraViewRef.current;
    const x = camera.centerX + (clientX - rect.left - rect.width / 2) / (content.width * camera.zoom);
    const y = camera.centerY + (clientY - rect.top - rect.height / 2) / (content.height * camera.zoom);
    if (x < 0 || x > 1 || y < 0 || y > 1) return null;
    return { x, y, width: content.width, height: content.height };
  };

  const getCellAtClientPosition = (clientX, clientY) => {
    const point = getWorldPoint(clientX, clientY);
    if (!point) return null;
    if (gridType === 'hex') {
      return nearestHexCell(
        point.x * point.width,
        point.y * point.height,
        point.width,
        point.height,
        gridCols,
        gridRows
      );
    }
    return {
      col: Math.min(gridCols - 1, Math.floor(point.x * gridCols)),
      row: Math.min(gridRows - 1, Math.floor(point.y * gridRows)),
    };
  };

  const recordPaintCell = (x, y, add) => {
    const key = `${x}|${y}`;
    if (editingCellSet.has(key) === add) paintChangesRef.current.delete(key);
    else paintChangesRef.current.set(key, { x, y, add });
    setLivePaintChanges(Array.from(paintChangesRef.current.values()));
  };

  const handlePaintPointerDown = (event) => {
    if (!editingEffect || !onPaintBattleEffectCells || event.button !== 0) return;
    const cell = getCellAtClientPosition(event.clientX, event.clientY);
    if (!cell) return;
    event.preventDefault();
    event.stopPropagation();
    paintChangesRef.current.clear();
    const key = `${cell.col}|${cell.row}`;
    const intentAdd = !editingCellSet.has(key);
    paintStateRef.current = { active: true, add: intentAdd };
    lastPaintedHexRef.current = key;
    recordPaintCell(cell.col, cell.row, intentAdd);
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      paintStateRef.current.active = false;
    }
  };

  const handlePaintPointerMove = (event) => {
    if (!editingEffect || !paintStateRef.current.active) return;
    const cell = getCellAtClientPosition(event.clientX, event.clientY);
    if (!cell) return;
    const key = `${cell.col}|${cell.row}`;
    if (key === lastPaintedHexRef.current) return;
    lastPaintedHexRef.current = key;
    event.preventDefault();
    recordPaintCell(cell.col, cell.row, paintStateRef.current.add);
  };

  const stopPaintGesture = (event) => {
    if (!paintStateRef.current.active) return;
    event?.preventDefault?.();
    const changes = Array.from(paintChangesRef.current.values());
    if (changes.length && editingEffect && onPaintBattleEffectCells) {
      onPaintBattleEffectCells(editingEffect.id, changes);
    }
    paintChangesRef.current.clear();
    setLivePaintChanges([]);
    paintStateRef.current = { active: false, add: paintStateRef.current.add };
    lastPaintedHexRef.current = null;
    suppressNextClickRef.current = true;
  };

  // ─── Draw Layer Handlers ───────────────────────────────────────────────────
  const getBoardPoint = (clientX, clientY) => {
    const worldPoint = getWorldPoint(clientX, clientY);
    if (!worldPoint) return null;
    return {
      x: worldPoint.x * 100,
      y: worldPoint.y * 100,
    };
  };

  const handleDrawPointerDown = (event, layerId) => {
    if (readOnly || drawingLayerId !== layerId) return;
    event.preventDefault();
    event.stopPropagation();
    const pt = getBoardPoint(event.clientX, event.clientY);
    if (!pt) return;
    drawStrokeRef.current = { layerId, pointerId: event.pointerId, points: [pt] };
    setLiveStroke({ layerId, points: [pt] });
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // ignore
    }
  };

  const handleDrawPointerMove = (event, layerId) => {
    if (readOnly || drawingLayerId !== layerId) return;
    const stroke = drawStrokeRef.current;
    if (!stroke || stroke.layerId !== layerId) return;
    if (stroke.pointerId !== null && stroke.pointerId !== undefined && event.pointerId !== stroke.pointerId) return;
    event.preventDefault();
    const pt = getBoardPoint(event.clientX, event.clientY);
    if (!pt) return;
    const newPoints = [...stroke.points, pt];
    drawStrokeRef.current = { ...stroke, points: newPoints };
    setLiveStroke({ layerId, points: newPoints });
  };

  const handleDrawPointerUp = (event, layerId) => {
    if (readOnly) return;
    const stroke = drawStrokeRef.current;
    if (!stroke || stroke.layerId !== layerId) return;
    event.preventDefault();
    if (stroke.points.length >= 2 && typeof onDrawLayerStroke === 'function') {
      const layer = scene.layers.find((l) => l.id === layerId);
      onDrawLayerStroke(layerId, {
        color: layer?.drawColor || '#e94f37',
        width: Number(layer?.strokeWidth) || 4,
        points: stroke.points,
      });
    }
    drawStrokeRef.current = null;
    setLiveStroke(null);
    suppressNextClickRef.current = true;
  };

  // ─── Text Layer Drag Handlers ──────────────────────────────────────────────
  const handleTextPointerDown = (event, layer) => {
    if (readOnly || !onMoveTextLayer) return;
    event.preventDefault();
    event.stopPropagation();
    textDragRef.current = {
      layerId: layer.id,
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startLayerX: layer.x ?? 50,
      startLayerY: layer.y ?? 50,
      zoom: cameraViewRef.current.zoom,
    };
    document.body.classList.add('layer-text-dragging');
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // ignore
    }
  };

  const handleTextPointerMove = (event, layerId) => {
    if (readOnly) return;
    const drag = textDragRef.current;
    if (!drag || drag.layerId !== layerId) return;
    if (drag.pointerId != null && event.pointerId !== drag.pointerId) return;
    event.preventDefault();
    const board = localRef.current;
    if (!board) return;
    const rect = board.getBoundingClientRect();
    const content = getGridContent(rect.width, rect.height);
    const dx = ((event.clientX - drag.startClientX) / (content.width * drag.zoom)) * 100;
    const dy = ((event.clientY - drag.startClientY) / (content.height * drag.zoom)) * 100;
    const newX = Math.max(0, Math.min(100, drag.startLayerX + dx));
    const newY = Math.max(0, Math.min(100, drag.startLayerY + dy));
    setTextDragPos({ layerId, x: newX, y: newY });
  };

  const handleTextPointerUp = (event, layerId) => {
    if (readOnly) return;
    const drag = textDragRef.current;
    if (!drag || drag.layerId !== layerId) return;
    event.preventDefault();
    document.body.classList.remove('layer-text-dragging');
    const board = localRef.current;
    if (board && typeof onMoveTextLayer === 'function') {
      const rect = board.getBoundingClientRect();
      const content = getGridContent(rect.width, rect.height);
      const dx = ((event.clientX - drag.startClientX) / (content.width * drag.zoom)) * 100;
      const dy = ((event.clientY - drag.startClientY) / (content.height * drag.zoom)) * 100;
      const newX = Math.max(0, Math.min(100, drag.startLayerX + dx));
      const newY = Math.max(0, Math.min(100, drag.startLayerY + dy));
      onMoveTextLayer(layerId, newX, newY);
    }
    textDragRef.current = null;
    setTextDragPos(null);
    suppressNextClickRef.current = true;
  };

  const handleGridDoubleClick = (event) => {
    const pingHandler = onPingDoubleClick || onPingGrid;
    if (
      !pingHandler
      || editingEffect
      || (!onPingDoubleClick && event.target.closest('.token'))
      || !localRef.current
    ) return;
    if (suppressNextDoubleClickRef.current) {
      suppressNextDoubleClickRef.current = false;
      return;
    }
    const cell = getCellAtClientPosition(event.clientX, event.clientY);
    if (cell) {
      event.preventDefault();
      pingHandler(cell.col, cell.row);
    }
  };

  const handleCameraFrame = (camera) => {
    const board = localRef.current;
    if (!board) return;
    const width = board.clientWidth;
    const height = board.clientHeight;
    const content = getGridContent(width, height);
    const translateX = camera.zoom * (0.5 - camera.centerX) * content.width;
    const translateY = camera.zoom * (0.5 - camera.centerY) * content.height;
    [worldOverlayRef.current, viewportOverlayRef.current].forEach((overlay) => {
      if (!overlay) return;
      overlay.style.left = `${(width - content.width) / 2}px`;
      overlay.style.top = `${(height - content.height) / 2}px`;
      overlay.style.width = `${content.width}px`;
      overlay.style.height = `${content.height}px`;
      overlay.style.right = 'auto';
      overlay.style.bottom = 'auto';
      overlay.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${camera.zoom})`;
    });
    cameraViewRef.current = camera;
  };

  const handleCameraPointerDown = (event) => {
    if (!cameraInteractive || !onViewStateChange || event.button !== 0 || editingEffect) return;
    const isTouchPointer = event.pointerType === 'touch';
    if (event.target.closest('.layer_text, .layer_draw_svg, .battle_effect_edit_layer, .display_viewport_preview')) return;
    if (!isTouchPointer && event.target.closest('.token')) return;
    const camera = cameraViewRef.current;
    const rect = localRef.current?.getBoundingClientRect();
    const content = rect ? getGridContent(rect.width, rect.height) : null;
    const hasCoverOverflow = fitMode === 'cover' && rect && content && (
      content.width > rect.width + 1 || content.height > rect.height + 1
    );
    if (camera.zoom <= 1 && !hasCoverOverflow) return;
    event.preventDefault();
    cameraDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startView: camera,
      moved: false,
    };
    try {
      event.currentTarget.setPointerCapture?.(event.pointerId);
    } catch {
      cameraDragRef.current = null;
    }
  };

  const handleCameraPointerMove = (event) => {
    const drag = cameraDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId || !onViewStateChange) return;
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (Math.abs(deltaX) + Math.abs(deltaY) > 3) {
      drag.moved = true;
      suppressNextClickRef.current = true;
    }
    const rect = localRef.current?.getBoundingClientRect();
    if (!rect?.width || !rect?.height) return;
    const content = getGridContent(rect.width, rect.height);
    const nextView = normalizeGridViewState({
      ...drag.startView,
      centerX: drag.startView.centerX - deltaX / (content.width * drag.startView.zoom),
      centerY: drag.startView.centerY - deltaY / (content.height * drag.startView.zoom),
    }, getCameraViewport(rect));
    cameraViewRef.current = nextView;
    onViewStateChange(nextView);
  };

  const handleCameraPointerUp = (event) => {
    if (cameraDragRef.current?.pointerId !== event.pointerId) return;
    cameraDragRef.current = null;
  };

  const handleCameraWheel = (event) => {
    if (!cameraWheelZoom || !onViewStateChange || !event.deltaY || !localRef.current) return;
    event.preventDefault();
    const rect = localRef.current.getBoundingClientRect();
    const camera = cameraViewRef.current;
    const content = getGridContent(rect.width, rect.height);
    const pointerX = event.clientX - rect.left - rect.width / 2;
    const pointerY = event.clientY - rect.top - rect.height / 2;
    const zoom = Math.min(MAX_GRID_ZOOM, Math.max(1, camera.zoom * Math.exp(-event.deltaY * 0.001)));
    const anchorX = camera.centerX + pointerX / (content.width * camera.zoom);
    const anchorY = camera.centerY + pointerY / (content.height * camera.zoom);
    const nextView = normalizeGridViewState({
      zoom,
      centerX: anchorX - pointerX / (content.width * zoom),
      centerY: anchorY - pointerY / (content.height * zoom),
    }, getCameraViewport(rect));
    cameraViewRef.current = nextView;
    onViewStateChange(nextView);
  };

  return (
    <div
      className={`grid_board${readOnly ? ' display_only' : ''}${cameraInteractive ? ' grid_board_camera_interactive' : ''}`}
      ref={attachRef}
      style={{
        aspectRatio: gridType === 'hex'
          ? `${hexSvgW} / ${hexSvgH}`
          : `${gridCols} / ${gridRows}`,
        ...(boardDimensions ? {
          width: `${boardDimensions.width}px`,
          height: `${boardDimensions.height}px`,
          maxWidth: 'none',
          maxHeight: 'none',
          flex: '0 0 auto',
        } : null),
        ...(boardTransform ? { transform: boardTransform, transformOrigin: 'center center' } : null),
      }}
      onPointerDown={cameraInteractive ? handleCameraPointerDown : undefined}
      onPointerMove={cameraInteractive ? handleCameraPointerMove : undefined}
      onPointerUp={cameraInteractive ? handleCameraPointerUp : undefined}
      onPointerCancel={cameraInteractive ? handleCameraPointerUp : undefined}
      onWheel={cameraWheelZoom ? handleCameraWheel : undefined}
      onClick={(event) => {
        if (suppressNextClickRef.current) {
          suppressNextClickRef.current = false;
          suppressNextDoubleClickRef.current = true;
          window.setTimeout(() => {
            suppressNextDoubleClickRef.current = false;
          }, 500);
          return;
        }
        if (!readOnly && typeof onSelectToken === 'function') {
          onSelectToken(null);
        }
      }}
      onDoubleClick={handleGridDoubleClick}
    >
      <GridWorldCanvas
        gridType={gridType}
        gridCols={gridCols}
        gridRows={gridRows}
        viewState={viewState}
        fitMode={fitMode}
        onCameraFrame={handleCameraFrame}
      />
      <div className='grid_board_world_overlays' ref={worldOverlayRef}>

      {scene.layers
        .filter((layer) => layer.visible)
        .sort((a, b) => (a.z || 0) - (b.z || 0))
        .map((layer) => {
          const layerType = layer.type || 'image';

          if (layerType === 'text') {
            const isDragging =
              textDragPos && textDragPos.layerId === layer.id;
            const posX = isDragging ? textDragPos.x : (layer.x ?? 50);
            const posY = isDragging ? textDragPos.y : (layer.y ?? 50);
            return (
              <div
                key={layer.id}
                className={`layer_text${readOnly ? ' layer_text_readonly' : ''}${selectedLayerId === layer.id ? ' layer_flip_selected' : ''}`}
                style={{
                  left: `${posX}%`,
                  top: `${posY}%`,
                  fontFamily: layer.font || 'sans-serif',
                  fontSize: `${Number(layer.fontSize) || 36}px`,
                  color: layer.color || '#ffffff',
                  zIndex: (layer.z || 1) + 10,
                }}
                onPointerDown={
                  !readOnly
                    ? (e) => handleTextPointerDown(e, layer)
                    : undefined
                }
                onPointerMove={
                  !readOnly
                    ? (e) => handleTextPointerMove(e, layer.id)
                    : undefined
                }
                onPointerUp={
                  !readOnly
                    ? (e) => handleTextPointerUp(e, layer.id)
                    : undefined
                }
                onPointerCancel={
                  !readOnly
                    ? (e) => handleTextPointerUp(e, layer.id)
                    : undefined
                }
              >
                {layer.text || ''}
              </div>
            );
          }

          if (layerType === 'draw') {
            const isDrawing =
              !readOnly && drawingLayerId === layer.id;
            const currentLive =
              liveStroke && liveStroke.layerId === layer.id
                ? liveStroke
                : null;
            return (
              <svg
                key={layer.id}
                className={`layer_draw_svg${isDrawing ? ' draw_active' : ''}${selectedLayerId === layer.id ? ' layer_flip_selected' : ''}`}
                viewBox='0 0 100 100'
                preserveAspectRatio='none'
                style={{ zIndex: (layer.z || 1) + 10 }}
                onPointerDown={
                  isDrawing
                    ? (e) => handleDrawPointerDown(e, layer.id)
                    : undefined
                }
                onPointerMove={
                  isDrawing
                    ? (e) => handleDrawPointerMove(e, layer.id)
                    : undefined
                }
                onPointerUp={
                  isDrawing
                    ? (e) => handleDrawPointerUp(e, layer.id)
                    : undefined
                }
                onPointerCancel={
                  isDrawing
                    ? (e) => handleDrawPointerUp(e, layer.id)
                    : undefined
                }
              >
                {(layer.paths || []).map((path, i) => (
                  <polyline
                    key={i}
                    points={(path.points || [])
                      .map((p) => `${p.x},${p.y}`)
                      .join(' ')}
                    stroke={path.color || '#e94f37'}
                    strokeWidth={path.width || 4}
                    fill='none'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    vectorEffect='non-scaling-stroke'
                  />
                ))}
                {currentLive && currentLive.points.length >= 2 && (
                  <polyline
                    points={currentLive.points
                      .map((p) => `${p.x},${p.y}`)
                      .join(' ')}
                    stroke={layer.drawColor || '#e94f37'}
                    strokeWidth={Number(layer.strokeWidth) || 4}
                    fill='none'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    vectorEffect='non-scaling-stroke'
                    opacity={0.85}
                  />
                )}
              </svg>
            );
          }

          // Default: image layer
          const url = layerUrlMap[layer.id] || null;
          return (
            <div key={layer.id} className='layer_wrapper'>
              {layer.bgColor && (
                <div
                  className='layer_bg'
                  style={{ background: layer.bgColor }}
                />
              )}
              {url && (
                <img
                  src={url}
                  alt=''
                  className={`layer_img${selectedLayerId === layer.id ? ' layer_flip_selected' : ''}`}
                />
              )}
            </div>
          );
        })}

      {rangeMeasurements.length > 0 && (
        <svg
          className='range_overlay'
          viewBox={rangeMeasurements[0] ? `0 0 ${rangeMeasurements[0].viewW} ${rangeMeasurements[0].viewH}` : `0 0 ${gridCols} ${gridRows}`}
          preserveAspectRatio='none'
        >
          {rangeMeasurements.map((range) => {
            const lineStrokeW = (range.viewW * 0.006) / Math.max(Number(boardOverlayScale) || 1, 0.01);
            const fontSize = range.viewW * 0.014;
            const strokeWidth = range.viewW * 0.004;
            return (
              <g key={range.targetId}>
                <line
                  x1={range.lineStart.x}
                  y1={range.lineStart.y}
                  x2={range.lineEnd.x}
                  y2={range.lineEnd.y}
                  stroke={range.color}
                  strokeWidth={lineStrokeW}
                  strokeLinecap='round'
                />
                <g
                  transform={`translate(${range.labelX} ${range.labelY}) scale(${overlayCounterScale}) translate(${-range.labelX} ${-range.labelY})`}
                >
                  <text
                    x={range.labelX}
                    y={range.labelY}
                    transform={`rotate(${range.rotation} ${range.labelX} ${range.labelY})`}
                    style={{
                      fill: range.color,
                      fontSize,
                      fontWeight: 1000,
                      paintOrder: 'stroke',
                      stroke: 'rgba(0,0,0,1)',
                      strokeWidth,
                      letterSpacing: 0.01,
                      textTransform: 'uppercase',
                    }}
                  >
                    {`${range.feet} – ${range.label}`}
                  </text>
                </g>
              </g>
            );
          })}
        </svg>
      )}

      {renderedBattleEffects.length > 0 && (
        <GridBattleEffectsCanvas
          effects={renderedBattleEffects}
          gridCols={gridCols}
          gridRows={gridRows}
          gridType={gridType}
        />
      )}

      {pings && pings.length > 0 && (
        <div className='ping_layer'>
          {pings.map((ping) => {
            let pingStyle;
            if (gridType === 'hex') {
              const { pctX, pctY } = hexCellToPercent(ping.x, ping.y, gridCols, gridRows);
              pingStyle = { left: `${pctX}%`, top: `${pctY}%` };
            } else {
              pingStyle = {
                left: `calc(${ping.x + 0.5} * (100% / ${gridCols}))`,
                top: `calc(${ping.y + 0.5} * (100% / ${gridRows}))`,
              };
            }
            const sourceClass = ping.source === 'player' ? ' grid_ping_player' : ' grid_ping_gm';
            const pingCounterScale = 1 / Math.max(Number(pingZoomScale) || 1, 0.01);
            return (
              <div
                key={ping.id}
                className={`grid_ping${sourceClass}`}
                style={{ ...pingStyle, '--ping-counter-scale': pingCounterScale }}
              >
                <div className='ping_pulse' />
                <div className='ping_marker' />
              </div>
            );
          })}
        </div>
      )}

      {scene.tokens
        .filter(token => token.visible !== false)
        .map(token => {
          const tokenSize = getTokenGridUnits(token.size);
          const tokenInitialFontSize = getTokenInitialFontSize(gridCols);
          const presenting = isPresentingSizeValue(token.size);
          let widthValue, heightValue, nonDisplayStyle;
          if (gridType === 'hex') {
            widthValue = `${tokenSize * hexCellWPct}%`;
            heightValue = `${tokenSize * hexCellHPct}%`;
            const { pctX, pctY } = hexCellToPercent(token.x, token.y, gridCols, gridRows);
            nonDisplayStyle = {
              left: `${pctX - tokenSize * hexCellWPct / 2}%`,
              top: `${pctY - tokenSize * hexCellHPct / 2}%`,
            };
          } else {
            widthValue = `calc(${tokenSize} * (100% / ${gridCols}))`;
            heightValue = `calc(${tokenSize} * (100% / ${gridRows}))`;
            nonDisplayStyle = {
              left: `calc(${token.x} * (100%/${gridCols}))`,
              top: `calc(${token.y} * (100%/${gridRows}))`,
            };
          }
          const isDisplay = readOnly;
          const transformStyle = isDisplay
            ? tokenTransforms[token.id]
              ? { transform: `translate3d(${tokenTransforms[token.id].x}px, ${tokenTransforms[token.id].y}px, 0)` }
              : { transform: 'translate3d(0,0,0)' }
            : nonDisplayStyle;
          const enteringActive = enteringTokens.some(e => e.id === token.id) ? ' token_enter' : '';
          const presentingClass = presenting ? ' token_presenting' : '';
          const selectedClass = (typeof selectedTokenId !== 'undefined' && selectedTokenId === token.id) ? ' token_selected' : '';
          
          return (
            <div
              key={token.id}
              className={`token${token.grayscale ? ' grayscale' : ''}${isDisplay && animateMoves ? ' display_anim' : ''}${enteringActive}${presentingClass}${selectedClass}`}
              style={{
                ...transformStyle,
                ...(dragVisual && dragVisual.id === token.id && !isDisplay
                  ? { transform: `translate3d(${dragVisual.dx}px, ${dragVisual.dy}px, 0)` }
                  : null),
                zIndex: token.zIndex || 1000,
                background: tokenUrls[token.id] ? 'transparent' : '#e94f37',
                width: widthValue,
                height: heightValue,
                boxShadow: (typeof selectedTokenId !== 'undefined' && selectedTokenId === token.id) ? '0 0 0 3px #b1594c75' : undefined,
                pointerEvents: editingEffect ? 'none' : undefined,
              }}
              draggable={false}
              onPointerDown={!isDisplay ? (event) => handleTokenPointerDown(event, token.id) : undefined}
              onPointerMove={!isDisplay ? (event) => handleTokenPointerMove(event, token.id) : undefined}
              onPointerUp={!isDisplay ? (event) => finishTokenDrag(event, token.id) : undefined}
              onPointerCancel={!isDisplay ? (event) => finishTokenDrag(event, token.id) : undefined}
              onClick={!isDisplay && typeof onSelectToken === 'function' ? (e) => { e.stopPropagation(); onSelectToken(token.id); } : undefined}
              onDoubleClick={
                onTokenDoubleClick
                  ? (event) => {
                      event.stopPropagation();
                      onTokenDoubleClick(token.id);
                    }
                  : undefined
              }
            >
              <div className='token_inner'>
                {tokenUrls[token.id] && (
                  <GridTokenImageCanvas
                    src={tokenUrls[token.id]}
                    zoom={viewState.zoom}
                    className='token_img'
                  />
                )}
                {token.showInitial !== false && (
                  <span
                    className='token_label'
                    style={{
                      fontSize: `${tokenInitialFontSize}px`,
                      letterSpacing: `${Math.min(0.5, tokenInitialFontSize * 0.04)}px`,
                    }}
                  >
                    {(token.name || '').trim().charAt(0).toUpperCase() || 'T'}
                  </span>
                )}
              </div>
              {!presenting && Array.isArray(token.conditions) && token.conditions.length > 0 && (
                <TokenConditionRing conditions={token.conditions} system={scene.ttrpgSystem} tokenId={token.id} />
              )}
            </div>
          );
        })}
      {/* Exiting tokens for fade-out */}
      {exitingTokens.map(({ id, token, lastDisplayPos }) => {
        const isDisplay = readOnly;
        const tokenSize = getTokenGridUnits(token.size);
        const tokenInitialFontSize = getTokenInitialFontSize(gridCols);
        let widthValue, heightValue, fallbackStyle;
        if (gridType === 'hex') {
          widthValue = `${tokenSize * hexCellWPct}%`;
          heightValue = `${tokenSize * hexCellHPct}%`;
          const { pctX, pctY } = hexCellToPercent(token.x, token.y, gridCols, gridRows);
          fallbackStyle = {
            left: `${pctX - tokenSize * hexCellWPct / 2}%`,
            top: `${pctY - tokenSize * hexCellHPct / 2}%`,
          };
        } else {
          widthValue = `calc(${tokenSize} * (100% / ${gridCols}))`;
          heightValue = `calc(${tokenSize} * (100% / ${gridRows}))`;
          fallbackStyle = {
            left: `calc(${token.x} * (100%/${gridCols}))`,
            top: `calc(${token.y} * (100%/${gridRows}))`,
          };
        }
        const presentingClass = isPresentingSizeValue(token.size) ? ' token_presenting' : '';
        const exitPos = (isDisplay && (tokenTransforms[id] || lastDisplayPos)) || null;
        const transformStyle = exitPos
          ? { transform: `translate3d(${exitPos.x}px, ${exitPos.y}px, 0)` }
          : fallbackStyle;
        return (
          <div
            key={`exit-${id}`}
            className={`token token_exit${token.grayscale ? ' grayscale' : ''}${isDisplay && animateMoves ? ' display_anim' : ''}${presentingClass}`}
            style={{
              ...transformStyle,
              zIndex: (token.zIndex || 1000),
              background: tokenUrls[id] ? 'transparent' : '#e94f37',
              width: widthValue,
              height: heightValue,
              pointerEvents: editingEffect ? 'none' : undefined,
            }}
          >
            <div className='token_inner'>
              {tokenUrls[id] && (
                <GridTokenImageCanvas
                  src={tokenUrls[id]}
                  zoom={viewState.zoom}
                  className='token_img'
                />
              )}
              {token.showInitial !== false && (
                <span
                  className='token_label'
                  style={{
                    fontSize: `${tokenInitialFontSize}px`,
                    letterSpacing: `${Math.min(0.5, tokenInitialFontSize * 0.04)}px`,
                  }}
                >
                  {(token.name || '').trim().charAt(0).toUpperCase() || 'T'}
                </span>
              )}
            </div>
            {!isPresentingSizeValue(token.size) && Array.isArray(token.conditions) && token.conditions.length > 0 && (
              <TokenConditionRing conditions={token.conditions} system={scene.ttrpgSystem} tokenId={id} />
            )}
          </div>
        );
      })}
      {scene.effect?.type && <EffectRenderer effect={scene.effect.type} />}

      {editingEffect && (
        <div
          className='battle_effect_edit_layer'
          onPointerDown={handlePaintPointerDown}
          onPointerMove={handlePaintPointerMove}
          onPointerUp={stopPaintGesture}
          onPointerCancel={stopPaintGesture}
        />
      )}

      {namePositions.length > 0 && (
        <svg
          className='token_names_overlay'
          viewBox={`0 0 ${namePositions[0]?.viewW ?? gridCols} ${namePositions[0]?.viewH ?? gridRows}`}
          preserveAspectRatio='none'
        >
          {namePositions.map(({ id, labelX, labelY, name, fontSize }) => (
            <g
              key={id}
              transform={`translate(${labelX} ${labelY}) scale(${overlayCounterScale}) translate(${-labelX} ${-labelY})`}
            >
              <text
                x={labelX}
                y={labelY}
                textAnchor='middle'
                dominantBaseline='middle'
                style={{
                  fill: '#fff',
                  fontSize: fontSize ?? 0.45,
                  fontWeight: 700,
                  paintOrder: 'stroke',
                  stroke: 'rgba(0,0,0,0.85)',
                  strokeWidth: (fontSize ?? 0.45) * 0.27,
                  letterSpacing: 0.01,
                }}
              >
                {name}
              </text>
            </g>
          ))}
        </svg>
      )}

      {!readOnly && dhTooltipTokenId && (() => {
        const dhToken = scene.tokens.find(
          (t) => t.id === dhTooltipTokenId && t.visible !== false
        );
        if (!dhToken) return null;

        const tokenSize = getTokenGridUnits(dhToken.size);
        const tooltipStyle = getGridTooltipPosition(
          dhToken,
          tokenSize,
          gridCols,
          gridRows
        );

        return (
          <div
            key={dhToken.id}
            style={{
              ...tooltipStyle,
              transform: `scale(${overlayCounterScale})`,
              transformOrigin: tooltipStyle.left ? 'top left' : 'top right',
            }}
          >
            <DaggerheartHealthTooltip
              token={dhToken}
              onUpdate={onDhTokenUpdate}
              onClose={onDhTooltipClose}
              readOnly={readOnly}
            />
          </div>
        );
      })()}
      </div>
      {viewportPreviewStore && (
        <div className='grid_board_viewport_overlay' ref={viewportOverlayRef}>
          <GridViewportPreview
            viewportStore={viewportPreviewStore}
            boardRef={localRef}
            interactive={displayViewportInteractive}
            alwaysVisible={keepDisplayViewportPreview}
            onMove={onMoveDisplayViewport}
          />
        </div>
      )}
      <Loading active={isLoading} mode='dark' overlay />
    </div>
  );
}));

export default GridBoard;
